import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';

import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { OnboardingLayout } from './components/layout/OnboardingLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { RootLayout } from './components/layout/RootLayout';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { Dashboard } from './pages/Dashboard';
import { ErrorPage } from './pages/ErrorPage';
import { Faq } from './pages/Faq';
import { Landing } from './pages/Landing';
import { NotFound } from './pages/NotFound';
import { Privacy } from './pages/Privacy';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Terms } from './pages/Terms';
import { Accounts } from './pages/accounts/Accounts';
import { Budget } from './pages/budget/Budget';
import { AnnualCalendar } from './pages/calendar/AnnualCalendar';
import { Categories } from './pages/categories/Categories';
import { CategoryDetail } from './pages/categories/CategoryDetail';
import { Expenses } from './pages/expenses/Expenses';
import { ExpensesQuickOpen } from './pages/expenses/ExpensesQuickOpen';
import { GoalDetail } from './pages/goals/GoalDetail';
import { Goals } from './pages/goals/Goals';
import { SmartGoalWizard } from './pages/goals/SmartGoalWizard';
import { Dividas } from './pages/dividas/Dividas';
import { DividasEvaluator } from './pages/dividas/DividasEvaluator';
import { DividasPlan } from './pages/dividas/DividasPlan';
import { DividasSimulator } from './pages/dividas/DividasSimulator';
import { Patrimonio } from './pages/patrimonio/Patrimonio';
import { Pots } from './pages/pots/Pots';
import { SelfieNew } from './pages/selfie/SelfieNew';
import { SelfieResult } from './pages/selfie/SelfieResult';
import { SelfieReport } from './pages/selfie/SelfieReport';
import { SelfieHistory } from './pages/selfie/SelfieHistory';
import { CycleSettings } from './pages/cycle/CycleSettings';
import { SelfieEdit } from './pages/selfie/SelfieEdit';
import { IncomeDashboard } from './pages/income/IncomeDashboard';
import { IncomeNew } from './pages/income/IncomeNew';
import { IncomeSources } from './pages/income/IncomeSources';
import { EmergencyFundDashboard } from './pages/emergencyFund/EmergencyFundDashboard';
import { EmergencyFundWizard } from './pages/emergencyFund/EmergencyFundWizard';
import { PlanSimulator } from './pages/emergencyFund/PlanSimulator';
import { OnboardingAccounts } from './pages/onboarding/Accounts';
import { OnboardingCategorySelect } from './pages/onboarding/CategorySelect';
import { OnboardingCycleDay } from './pages/onboarding/CycleDay';
import { OnboardingDebtsSelect } from './pages/onboarding/DebtsSelect';
import { OnboardingIncomeSourcesSelect } from './pages/onboarding/IncomeSourcesSelect';
import { OnboardingMethod } from './pages/onboarding/Method';
import { OnboardingProfile } from './pages/onboarding/Profile';
import { OnboardingReady } from './pages/onboarding/Ready';
import { OnboardingWelcome } from './pages/onboarding/Welcome';
import { ConfirmEmailChange } from './pages/ConfirmEmailChange';
import { Appearance } from './pages/settings/Appearance';
import { SettingsOverview } from './pages/settings/SettingsOverview';
import { SettingsProfile } from './pages/settings/Profile';
import { AccountAccess } from './pages/settings/AccountAccess';
import { BudgetMethodSettings } from './pages/settings/BudgetMethodSettings';
import { CycleDaySettings } from './pages/settings/CycleDaySettings';
import { DeleteAccountSettings } from './pages/settings/DeleteAccountSettings';
import { SettingsHelp } from './pages/settings/Help';
import { Notifications } from './pages/settings/Notifications';
import { Reminders } from './pages/settings/Reminders';
import { paths, appPath } from './routes/paths';

function PtGoalDetailRedirect() {
  const { goalId } = useParams();
  return <Navigate to={paths.goal(goalId)} replace />;
}

function LegacyAppRedirect({ to }) {
  return <Navigate to={to} replace />;
}

function LegacyCategoryRedirect() {
  const { id } = useParams();
  return <Navigate to={paths.category(id)} replace />;
}

function LegacyGoalRedirect() {
  const { goalId } = useParams();
  return <Navigate to={paths.goal(goalId)} replace />;
}

function LegacySelfieResultRedirect() {
  const { id } = useParams();
  return <Navigate to={paths.selfieResult(id)} replace />;
}

function LegacySelfieEditRedirect() {
  const { id } = useParams();
  return <Navigate to={paths.selfieEdit(id)} replace />;
}

const appRoutes = [
  { index: true, element: <Navigate to={paths.dashboard} replace /> },
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'expenses/new', element: <ExpensesQuickOpen /> },
  { path: 'expenses', element: <Expenses /> },
  { path: 'categories', element: <Categories /> },
  { path: 'categories/:id', element: <CategoryDetail /> },
  { path: 'budget', element: <Budget /> },
  { path: 'goals', element: <Goals /> },
  { path: 'goals/new', element: <SmartGoalWizard /> },
  { path: 'goals/:goalId', element: <GoalDetail /> },
  { path: 'objectivos', element: <Navigate to={paths.goals} replace /> },
  { path: 'objectivos/:goalId', element: <PtGoalDetailRedirect /> },
  { path: 'fundo-emergencia', element: <Navigate to={paths.emergencyFund} replace /> },
  { path: 'pots', element: <Pots /> },
  { path: 'calendar', element: <AnnualCalendar /> },
  { path: 'income', element: <IncomeDashboard /> },
  { path: 'income/sources', element: <IncomeSources /> },
  { path: 'income/new', element: <IncomeNew /> },
  { path: 'emergency-fund', element: <EmergencyFundDashboard /> },
  { path: 'emergency-fund/wizard', element: <EmergencyFundWizard /> },
  {
    path: 'fundo-emergencia/wizard',
    element: <Navigate to={paths.emergencyFundWizard} replace />,
  },
  { path: 'emergency-fund/plano', element: <PlanSimulator /> },
  {
    path: 'fundo-emergencia/plano',
    element: <Navigate to={paths.emergencyFundPlano} replace />,
  },
  { path: 'ativos', element: <Accounts /> },
  { path: 'accounts', element: <Navigate to={paths.ativos} replace /> },
  { path: 'dividas', element: <Dividas /> },
  { path: 'motion', element: <Navigate to={paths.dividas} replace /> },
  { path: 'dividas/plano', element: <DividasPlan /> },
  { path: 'motion/plano', element: <Navigate to={paths.dividasPlan} replace /> },
  { path: 'dividas/avaliador', element: <DividasEvaluator /> },
  { path: 'motion/avaliador', element: <Navigate to={paths.dividasEvaluator} replace /> },
  { path: 'motion/simulador/:id', element: <DividasSimulator /> },
  { path: 'dividas/simulador/:id', element: <DividasSimulator /> },
  { path: 'patrimonio', element: <Patrimonio /> },
  { path: 'net-worth', element: <Navigate to={paths.patrimonio} replace /> },
  { path: 'selfie/new', element: <SelfieNew /> },
  { path: 'selfie/result/:id', element: <SelfieResult /> },
  { path: 'selfie/result/:id/report', element: <SelfieReport /> },
  { path: 'selfies', element: <SelfieHistory /> },
  { path: 'ciclo/definicoes', element: <CycleSettings /> },
  { path: 'selfie/historico', element: <Navigate to={paths.selfies} replace /> },
  { path: 'selfie/:id/edit', element: <SelfieEdit /> },
  { path: 'settings', element: <SettingsOverview />, loader: SettingsOverview.loader },
  { path: 'settings/profile', element: <SettingsProfile />, loader: SettingsProfile.loader },
  { path: 'settings/account', element: <AccountAccess />, loader: AccountAccess.loader },
  { path: 'settings/budget-method', element: <BudgetMethodSettings />, loader: BudgetMethodSettings.loader },
  { path: 'settings/cycle-day', element: <CycleDaySettings />, loader: CycleDaySettings.loader },
  { path: 'settings/help', element: <SettingsHelp /> },
  { path: 'settings/delete-account', element: <DeleteAccountSettings /> },
  { path: 'settings/notifications', element: <Notifications />, loader: Notifications.loader },
  { path: 'settings/reminders', element: <Reminders />, loader: Reminders.loader },
  { path: 'settings/appearance', element: <Appearance /> },
  { path: 'settings/*', element: <Navigate to={paths.settings} replace /> },
];

/** Redirects de bookmarks / links antigos (sem prefixo /app). */
const legacyAppRedirects = [
  'dashboard',
  'expenses',
  'expenses/new',
  'categories',
  'budget',
  'goals',
  'goals/new',
  'pots',
  'calendar',
  'income',
  'income/sources',
  'income/new',
  'emergency-fund',
  'emergency-fund/wizard',
  'emergency-fund/plano',
  'ativos',
  'accounts',
  'dividas',
  'dividas/plano',
  'motion/avaliador',
  'patrimonio',
  'net-worth',
  'selfie/new',
  'selfies',
  'ciclo/definicoes',
  'selfie/historico',
  'objectivos',
  'fundo-emergencia',
  'fundo-emergencia/wizard',
  'fundo-emergencia/plano',
  'settings',
].map((segment) => ({
  path: segment,
  element: <LegacyAppRedirect to={appPath(segment)} />,
}));

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: (
              <PublicRoute>
                <Landing />
              </PublicRoute>
            ),
          },
          { path: 'faq', element: <Faq /> },
          { path: 'contact', element: <Contact /> },
          { path: 'about', element: <About /> },
          { path: 'terms', element: <Terms /> },
          { path: 'privacy', element: <Privacy /> },
          {
            path: 'settings/notifications',
            element: <Navigate to={paths.settingsNotifications} replace />,
          },
        ],
      },
      {
        element: (
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        ),
        children: [
          { path: 'sign-in', element: <SignIn /> },
          { path: 'sign-up', element: <SignUp /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'reset-password/:uid/:token', element: <ResetPassword /> },
          { path: 'confirm-email-change', element: <ConfirmEmailChange /> },
        ],
      },
      {
        element: (
          <ProtectedRoute>
            <OnboardingLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: 'onboarding', element: <Navigate to="/onboarding/welcome" replace /> },
          { path: 'onboarding/welcome', element: <OnboardingWelcome /> },
          { path: 'onboarding/profile', element: <OnboardingProfile /> },
          { path: 'onboarding/cycle-day', element: <OnboardingCycleDay /> },
          {
            path: 'onboarding/method',
            element: <OnboardingMethod />,
            loader: OnboardingMethod.loader,
          },
          { path: 'onboarding/income-sources', element: <OnboardingIncomeSourcesSelect /> },
          { path: 'onboarding/categories', element: <OnboardingCategorySelect /> },
          { path: 'onboarding/accounts', element: <OnboardingAccounts /> },
          { path: 'onboarding/debts', element: <OnboardingDebtsSelect /> },
          { path: 'onboarding/ready', element: <OnboardingReady /> },
          // Legacy onboarding routes (bookmarks / mid-migration users).
          { path: 'onboarding/appearance', element: <Navigate to="/onboarding/profile" replace /> },
          { path: 'onboarding/method/intro', element: <Navigate to="/onboarding/method" replace /> },
          { path: 'onboarding/method/preset', element: <Navigate to="/onboarding/method" replace /> },
          { path: 'onboarding/method/customise', element: <Navigate to="/onboarding/method" replace /> },
          {
            path: 'onboarding/income-sources/intro',
            element: <Navigate to="/onboarding/income-sources" replace />,
          },
          {
            path: 'onboarding/income-sources/select',
            element: <Navigate to="/onboarding/income-sources" replace />,
          },
          {
            path: 'onboarding/categories/intro',
            element: <Navigate to="/onboarding/categories" replace />,
          },
          {
            path: 'onboarding/categories/select',
            element: <Navigate to="/onboarding/categories" replace />,
          },
          { path: 'onboarding/accounts/intro', element: <Navigate to="/onboarding/accounts" replace /> },
          { path: 'onboarding/accounts/first', element: <Navigate to="/onboarding/accounts" replace /> },
          { path: 'onboarding/debts-check', element: <Navigate to="/onboarding/debts" replace /> },
        ],
      },
      ...legacyAppRedirects,
      { path: 'categories/:id', element: <LegacyCategoryRedirect /> },
      { path: 'goals/:goalId', element: <LegacyGoalRedirect /> },
      { path: 'objectivos/:goalId', element: <PtGoalDetailRedirect /> },
      { path: 'selfie/result/:id', element: <LegacySelfieResultRedirect /> },
      { path: 'selfie/:id/edit', element: <LegacySelfieEditRedirect /> },
      {
        path: 'app',
        element: (
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        ),
        children: appRoutes,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
